import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { AnyTFunction } from '@/i18n/msg';
import { useAuth } from '@/contexts/AuthContext';
import { useStevensWidget } from '@/components/stevens-widget/StevensWidgetContext';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { COPILOT_NAME } from '@/lib/companion-config';
import { Input } from '@/components/ui/input';
import { PageHead, Emphasis, MonoLabel } from '@/components/editorial';
import { HelpCircle, Search } from 'lucide-react';
import type { ElementType } from 'react';
import { CrisisCallout } from '@/components/common/CrisisCallout';
import {
  buildTrferNavSections,
  TRFER_FOOTER_ITEMS,
  HELP_SELF_HREF,
  type TrferNavPermissions,
} from '@/components/layout/trfer-nav';
import { buildParentNavSections, SIDEBAR_FOOTER_ITEMS } from '@/components/layout/parent-nav';
import { buildAdminNavSections } from '@/components/layout/admin-nav';
import type { NavItemDef, NavSection } from '@/components/layout/sidebar-primitives';
import { TRFER_HELP_COPY } from './help-copy-trfer';
import { PARENT_HELP_COPY, helpCopyText } from './help-copy-parent';
import { NAV_KEY } from '@/i18n/keys/nav';
import { ADMIN_HELP_COPY } from './help-copy-admin';

interface HelpSection {
  icon: ElementType;
  title: string;
  description: string;
  /**
   * Nav group this surface sits in — carried straight off the nav module.
   *
   * Every role's cards now mirror that role's sidebar groups, because every
   * role's cards are now derived from that role's nav data. Mirroring is the
   * point: a reader looking for the page they just clicked finds it under the
   * heading they clicked it from. `undefined` is the sidebar's own unlabelled
   * top group (Today, Admin dashboard) and the footer tray, which render as
   * an unlabelled run exactly as they always did.
   */
  group?: string;
  /**
   * The memorial-register replacement for `description` (#685/#693).
   *
   * `null` withholds the whole section. `undefined` means the section reads
   * the same in both registers. Kept as a field on the section rather than as
   * a second array so the two registers sit side by side in one read — the
   * same discipline `bereavement-tone.ts` uses, and the reason the drift this
   * wave keeps finding is visible here instead of inferred from an absence.
   */
  memorial?: string | null;
}

interface RoleConfig {
  subtitle: string;
  sections: HelpSection[];
}

/**
 * Derive one role's help cards from that role's nav module (#611, #1191).
 *
 * One card per nav item the reader can actually see, in nav order, under the
 * nav's own group headings. Permission-gated surfaces are gated identically
 * here — documenting Requests to someone who cannot open it is the same
 * defect class as hiding Reimbursements from someone who can.
 *
 * `copyFor` resolves a route to its card body plus, where the register
 * differs, its memorial replacement. Missing copy resolves to an empty
 * description on purpose: `HelpPage.navParity.test.tsx` fails the build over
 * exactly that, with the route name in the failure message, rather than the
 * page shipping a blank card.
 *
 * The footer tray rides along ungrouped; Help does not document itself.
 */
function buildHelpSectionsFromNav(
  sections: NavSection[],
  footerItems: NavItemDef[],
  copyFor: (href: string) => { description: string; memorial?: string | null },
): HelpSection[] {
  const card = (item: NavItemDef, group?: string): HelpSection => ({
    icon: item.icon,
    title: item.name,
    group,
    ...copyFor(item.href),
  });

  return [
    ...sections.flatMap((section) => section.items.map((item) => card(item, section.label))),
    ...footerItems.filter((i) => i.href !== HELP_SELF_HREF).map((item) => card(item)),
  ];
}

/** The TRFer set — a flat route→string map, no memorial register. */
function buildTrferHelpSections(perms: TrferNavPermissions): HelpSection[] {
  return buildHelpSectionsFromNav(buildTrferNavSections(perms), TRFER_FOOTER_ITEMS, (href) => ({
    description: TRFER_HELP_COPY[href] ?? '',
  }));
}

/**
 * The parent set, resolved against the reader's own companion persona.
 *
 * Built from the WIDEST parent nav (`showHealth: true`) rather than the
 * memorial-gated one, so the Health card is withheld by the copy module's
 * `memorial: null` — one withholding rule, stated beside the copy it
 * withholds, instead of two that have to agree.
 */
function buildParentHelpSections(companionName: string, t: AnyTFunction): HelpSection[] {
  return buildHelpSectionsFromNav(
    buildParentNavSections({ showHealth: true }),
    SIDEBAR_FOOTER_ITEMS,
    (href) => {
      const entry = PARENT_HELP_COPY[href];
      return {
        description: entry ? helpCopyText(entry.description, companionName, t) : '',
        memorial:
          entry?.memorial === null
            ? null
            : entry?.memorial
              ? helpCopyText(entry.memorial, companionName, t)
              : undefined,
      };
    },
  );
}

/**
 * The admin set, gated on the same permission the admin sidebar gates on.
 *
 * Admins hold `can_review_applications` in practice, so this normally changes
 * nothing — but reading it rather than assuming it keeps help and nav gated by
 * one fact instead of two beliefs.
 */
function buildAdminHelpSections(canReviewApplications: boolean): HelpSection[] {
  return buildHelpSectionsFromNav(
    buildAdminNavSections({ canReviewApplications }),
    SIDEBAR_FOOTER_ITEMS,
    (href) => ({ description: ADMIN_HELP_COPY[href] ?? '' }),
  );
}

/**
 * The help copy, built per reader rather than frozen at module scope (#639).
 *
 * `companionName` is the PARENT's own companion persona — a family whose
 * companion is Maya must not read about Ember. It is threaded only through
 * the `parent` block. The `trfer` and `admin` blocks name no family
 * persona (staff copy spans a whole caseload); where they name an
 * assistant it is the staff copilot, `COPILOT_NAME` (William, #3066).
 */
function buildRoleConfig(
  companionName: string,
  trferPerms: TrferNavPermissions,
  t: AnyTFunction,
): Record<string, RoleConfig> {
  // The admin nav takes the same `canReviewApplications` flag the TRFer nav
  // does, so one value serves both role blocks below.
  const { canReviewApplications } = trferPerms;
  return {
    parent: {
      // The parent block is family copy and reads the help catalog (#2109
      // R3-B25). The trfer and admin subtitles below stay English until R4.
      subtitle: helpCopyText('help:page.parentSubtitle', companionName, t),
      // Derived from the nav, never hand-listed — see PARENT_HELP_COPY.
      sections: buildParentHelpSections(companionName, t),
    },
    trfer: {
      subtitle: 'Every surface in your sidebar, what it is scoped to, and what it does not do.',
      // Derived from the nav, never hand-listed — see TRFER_HELP_COPY (#611).
      sections: buildTrferHelpSections(trferPerms),
    },
    admin: {
      subtitle: `Learn about the platform admin tools and how ${COPILOT_NAME} can help you manage the system.`,
      // Derived from the nav, never hand-listed — see ADMIN_HELP_COPY.
      sections: buildAdminHelpSections(canReviewApplications),
    },
  };
}

/**
 * Resolves a role's sections into the register the reader is actually in.
 *
 * `null` withholds; a string replaces the description; `undefined` leaves the
 * section alone. Applied to every role, though only the parent set has
 * memorial variants — `useDashboardMode` no-ops for staff, so this is a
 * no-op there rather than a branch someone has to remember.
 */
function sectionsForRegister(sections: HelpSection[], isMemorial: boolean): HelpSection[] {
  if (!isMemorial) return sections;
  return sections
    .filter((s) => s.memorial !== null)
    .map((s) => (s.memorial ? { ...s, description: s.memorial } : s));
}

export function HelpPage() {
  const { t } = useTranslation(['help', 'nav', 'crisis']);
  const { user } = useAuth();
  const stevensWidget = useStevensWidget();
  const companionName = useCompanionName();
  const [search, setSearch] = useState('');

  const role = user?.role ?? 'parent';
  // Gate the staff help set on the same two flags the sidebar gates on, so a
  // non-lead never reads about a surface they cannot open (#611).
  const canAssignTrfers = user?.permissions?.can_assign_trfers === true;
  const canReviewApplications = user?.permissions?.can_review_applications === true;
  // `t` changes identity on a language switch (react-i18next binds it to the
  // language), and the nav names and help copy are resolved when this runs,
  // so the switch rebuilds them.
  const roleConfig = useMemo(
    () => buildRoleConfig(companionName, { canAssignTrfers, canReviewApplications }, t),
    [companionName, canAssignTrfers, canReviewApplications, t],
  );
  const baseConfig = roleConfig[role] ?? roleConfig.parent;
  const isParent = role === 'parent';

  // #685/#693 memorial gate. `modeLoading` counts as memorial for the same
  // reason it does in the sidebar: the fail-safe mode is 'active', so
  // rendering the Health card first and withdrawing it once the record lands
  // shows a bereaved parent the exact offer this gate exists to withhold.
  const { isMemorial, isLoading: modeLoading } = useDashboardMode();
  const config = useMemo<RoleConfig>(
    () => ({ ...baseConfig, sections: sectionsForRegister(baseConfig.sections, modeLoading || isMemorial) }),
    [baseConfig, modeLoading, isMemorial],
  );

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return config.sections;
    return config.sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q),
    );
  }, [config.sections, search]);

  /**
   * Collapse the flat list into consecutive runs sharing a group label,
   * preserving nav order. Roles whose sections carry no group (parent, admin)
   * produce exactly one unlabelled run, so they render as they always have.
   */
  const groupedSections = useMemo(() => {
    const runs: { label?: string; items: HelpSection[] }[] = [];
    for (const section of filteredSections) {
      const last = runs[runs.length - 1];
      if (last && last.label === section.group) last.items.push(section);
      else runs.push({ label: section.group, items: [section] });
    }
    return runs;
  }, [filteredSections]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      <PageHead
        eyebrowKey={NAV_KEY.help}
        icon={HelpCircle}
        title={<Trans t={t} i18nKey="page.title" components={{ em: <Emphasis>{null}</Emphasis> }} />}
        documentTitle={t('page.documentTitle')}
        lede={config.subtitle}
      />

      {/* Search-as-you-type — purely client-side over the static section
          list, so no debounce, no loading state. Hidden when there are
          fewer than 5 sections — search is cognitive overhead with so
          few items. */}
      {config.sections.length >= 5 && (
        <div className="relative max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stevens-ink-3 pointer-events-none" />
          <Input
            type="search"
            placeholder={t('page.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 focus-visible:border-stevens-indigo"
            aria-label={t('page.searchLabel')}
          />
        </div>
      )}

      {groupedSections.map((run, runIdx) => (
        <div key={run.label ?? `ungrouped-${runIdx}`} className="space-y-3">
          {run.label && (
            <MonoLabel className="block border-b border-border/40 pb-1.5 text-stevens-ink-3/70">
              {run.label}
            </MonoLabel>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {run.items.map((section) => {
              const Icon = section.icon;
              return (
            <article
              key={section.title}
              className="relative rounded-lg border border-border bg-card overflow-hidden flex flex-col"
            >
              <span
                className="absolute left-0 top-0 h-full w-[3px] bg-stevens-indigo"
                aria-hidden
              />
              <header className="flex items-start gap-3 px-5 pt-4 pb-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-stevens-indigo/10 text-stevens-indigo flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <MonoLabel className="block mb-1">
                    {t('page.sectionLabel')}
                  </MonoLabel>
                  <h3
                    className="text-lg leading-tight font-serif"
                  >
                    {section.title}
                  </h3>
                </div>
              </header>

              <p className="px-5 pb-3 text-body text-foreground/85 leading-relaxed whitespace-pre-line">
                {section.description}
              </p>

              <footer className="mt-auto flex items-center px-5 py-3 border-t border-border/50 bg-muted/20">
                <button
                  type="button"
                  // R3-B2: the prompt is written in the reader's UI language.
                  onClick={() => stevensWidget.open(t('page.askPrompt', { section: section.title }))}
                  className="text-body font-medium text-stevens-indigo hover:text-stevens-indigo-2 transition-colors"
                >
                  {t('page.ask', { companion: companionName })}
                </button>
              </footer>
            </article>
              );
            })}
          </div>
        </div>
      ))}

      {filteredSections.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p
            className="text-base italic text-stevens-ink-3 font-serif"
          >
            <Trans
              t={t}
              i18nKey="page.noResults"
              values={{ query: search }}
              components={{ query: <span translate="no" /> }}
            />
          </p>
          <button
            type="button"
            onClick={() => setSearch('')}
            className="mt-2 text-body text-stevens-indigo hover:text-stevens-indigo-2 font-medium"
          >
            {t('page.clearSearch')}
          </button>
        </div>
      )}

      {/* Crisis card — parents only. TRFers / helpers / admins have
          escalation routes in their own UIs and don't need a self-help
          crisis pathway here. Strict safety rule: AI never listed as a
          crisis resource. */}
      {/* The eyebrow override is the `crisis:helpEyebrow` key (#2109 R3 L6). */}
      {isParent && (
        <CrisisCallout
          eyebrow={t('crisis:helpEyebrow')}
        />
      )}
    </div>
  );
}
